/** What a place switch needs from the app — injected, so its ordering is testable without React. */
export type PlaceSwitchEffects = {
  /** The home's detail is already in the cache, so the switch can commit without a fetch. */
  isCached: (placeId: string) => boolean;
  /** Fetches the home's detail. It must settle by its own deadline; a rejection still commits. */
  load: (placeId: string) => Promise<unknown>;
  setPending: (placeId: string | undefined) => void;
  commit: (placeId: string) => void;
};

export type SwitchPlace = (
  placeId: string,
  onCommit?: () => Promise<void> | void,
) => Promise<void>;

/**
 * The member's place switch: fetch the home unless it is cached, settle `onCommit` (a URL
 * rewrite the new home's page must never see undone), then commit the id. Only the latest
 * call commits — a newer switch supersedes one still in flight.
 */
export function createPlaceSwitch(effects: PlaceSwitchEffects): SwitchPlace {
  let latest: string | undefined;

  return async (placeId, onCommit) => {
    latest = placeId;
    if (!effects.isCached(placeId)) {
      effects.setPending(placeId);
      await effects.load(placeId).catch(() => undefined);
      if (latest !== placeId) return;
    }
    await onCommit?.();
    if (latest !== placeId) return;
    latest = undefined;
    effects.commit(placeId);
    effects.setPending(undefined);
  };
}
